class TOSProjectStoriesIntro {
  constructor(section) {
    this.section = section;
    this.hasPlayed = false;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.modal = section.querySelector('[data-project-modal]');
    this.modalPanel = section.querySelector('.tos-project-modal__panel');
    this.modalImage = section.querySelector('[data-project-modal-image]');
    this.modalCategory = section.querySelector('[data-project-modal-category]');
    this.modalTitle = section.querySelector('[data-project-modal-title]');
    this.modalStory = section.querySelector('[data-project-modal-story]');
    this.modalChips = section.querySelector('[data-project-modal-chips]');
    this.modalReaction = section.querySelector('[data-project-modal-reaction]');
    this.lastFocusedElement = null;
    this.projects = this.getProjects();

    this.prepareKicker();
    this.mountModal();
    this.bindModalEvents();

    if (this.prefersReducedMotion) {
      this.showFinalState();
      return;
    }

    this.observe();
  }

  getProjects() {
    return {
      maa: {
        category: 'Personalised',
        title: 'A Letter to Mom',
        story: 'I wanted to gift my mother something that says everything I struggle to put into words.',
        product: 'Oversized Tee',
        occasion: 'Gift',
        style: 'Personalised',
        timeline: '3-5 Days',
        reaction: 'She didn\'t say much.\nShe just smiled... and hugged me.\nThat was exactly what I was hoping for.',
      },
      nala: {
        category: 'Personalised',
        title: 'A Pocket Full of Paws',
        story: 'I wanted something that reminded me of Nala even when she wasn\'t curled up beside me. I wasn\'t looking for just another cat T-shirt—I wanted to carry a little piece of my favourite companion wherever I went.',
        product: 'Oversized Tee',
        occasion: 'Pet Tribute',
        style: 'Illustrated Chest Print',
        timeline: '3-5 Days',
        reaction: 'Every time someone asks about the design, I end up talking about Nala. It became more than just a T-shirt—it became my favourite way to carry a little piece of her wherever I go.',
      },
      starboy: {
        category: 'Farewell Gift',
        title: 'One Last Encore',
        story: 'My friend was moving away, and I didn\'t want to give him something that would end up forgotten on a shelf. This design was built around our inside jokes, his love for performing, and the kind of energy that always made him the loudest person in the room. I wanted him to have something that would instantly remind him of home every time he wore it.',
        product: 'Oversized Tee',
        occasion: 'Farewell Gift',
        style: 'Custom Illustrated Graphic',
        timeline: '4-6 Days',
        reaction: 'He laughed the moment he unfolded it... then spent the next ten minutes pointing at every little detail we had hidden inside the artwork. Before leaving, he looked at me and said,\n\n"I\'m definitely wearing this on the first day in my new city."',
      },
      bulk: {
        category: 'Corporate Branding',
        title: 'Worn Beyond Work',
        story: 'We wanted our team\'s apparel to feel like something people would genuinely choose to wear—not just because it had our logo on it, but because it reflected who we are. Instead of creating ordinary company merchandise, we focused on clean, premium pieces that our team could wear confidently, both inside and outside the workplace.',
        product: 'Hoodie, Oversized Tee & Polo',
        occasion: 'Team Merchandise',
        style: 'Minimal Brand Identity',
        timeline: '10-14 Days',
        reaction: 'The best compliment came a week later when someone walked into the office wearing the hoodie on their day off. That\'s when we knew we hadn\'t just made company merchandise—we\'d created something people genuinely enjoyed wearing.',
      },
      vibes: {
        category: 'Personalised',
        title: 'Good Vibes Only',
        story: 'They wanted something playful that spread positivity without looking childish.',
        product: 'Oversized Tee',
        occasion: 'Personalised',
        style: 'Fun',
        timeline: '3-5 Days',
        reaction: 'It somehow made everyone smile before they even read the design.',
      },
    };
  }

  prepareKicker() {
    const kicker = this.section.querySelector('.tos-project-stories__kicker');
    if (!kicker || kicker.dataset.lettersReady === 'true') return;

    const text = kicker.textContent.trim();
    kicker.setAttribute('aria-label', text);
    kicker.textContent = '';

    Array.from(text).forEach((letter, index) => {
      const span = document.createElement('span');
      span.setAttribute('aria-hidden', 'true');
      span.style.setProperty('--tos-kicker-letter', index);
      span.innerHTML = letter === ' ' ? '&nbsp;' : letter;
      kicker.appendChild(span);
    });

    kicker.dataset.lettersReady = 'true';
  }

  mountModal() {
    if (!this.modal || this.modal.parentElement === document.body) return;
    document.body.appendChild(this.modal);
  }

  bindModalEvents() {
    this.section.querySelectorAll('[data-project-card]').forEach((card) => {
      card.addEventListener('click', () => this.openProject(card));
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.openProject(card);
        }
      });
    });

    this.modal?.querySelectorAll('[data-project-modal-close]').forEach((control) => {
      control.addEventListener('click', () => this.closeProject());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.modal?.classList.contains('is-open')) {
        this.closeProject();
      }
    });
  }

  openProject(card) {
    if (!this.modal) return;

    const project = this.projects[card.dataset.projectId];
    if (!project) return;

    this.lastFocusedElement = document.activeElement;
    this.populateModal(project, card);
    document.body.classList.add('tos-project-modal-open');
    this.modal.setAttribute('aria-hidden', 'false');
    this.modal.classList.add('is-open');
    this.modalPanel?.focus();
  }

  closeProject() {
    if (!this.modal) return;

    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('tos-project-modal-open');

    if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
      this.lastFocusedElement.focus();
    }
  }

  populateModal(project, card) {
    this.modalCategory.textContent = project.category;
    this.modalTitle.textContent = project.title;
    this.modalStory.textContent = project.story;
    this.modalReaction.textContent = project.reaction;

    this.modalChips.innerHTML = '';
    [
      ['Product', project.product],
      ['Occasion', project.occasion],
      ['Style', project.style],
      ['Timeline', project.timeline],
    ].forEach(([label, value]) => {
      const chip = document.createElement('span');
      chip.className = 'tos-project-modal__chip';
      chip.innerHTML = `<span class="tos-project-modal__chip-label">${label}</span><span class="tos-project-modal__chip-value">${value}</span>`;
      this.modalChips.appendChild(chip);
    });

    this.modalImage.innerHTML = '';
    const cardImage = card.querySelector('.tos-project-stories__image');
    const image = cardImage?.querySelector('img');

    if (image) {
      const visual = document.createElement('div');
      const modalImage = image.cloneNode(true);
      visual.className = 'tos-project-modal__visual';
      modalImage.removeAttribute('loading');
      visual.appendChild(modalImage);
      this.modalImage.appendChild(visual);
      return;
    }

    const visual = document.createElement('div');
    visual.className = 'tos-project-modal__visual tos-project-modal__visual--placeholder';
    visual.style.background = getComputedStyle(cardImage).background;
    this.modalImage.appendChild(visual);
  }

  observe() {
    if (!('IntersectionObserver' in window)) {
      this.play();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting || entry.intersectionRatio >= 0.08) {
            this.play();
            this.observer.disconnect();
          }
        });
      },
      {
        rootMargin: '0px 0px -8% 0px',
        threshold: [0, 0.08, 0.18, 0.38, 1],
      }
    );

    this.observer.observe(this.section);

    window.requestAnimationFrame(() => this.playIfVisible());
    window.setTimeout(() => this.playIfVisible(), 450);
    window.addEventListener('pageshow', () => this.playIfVisible(), { once: true });
    window.addEventListener('load', () => this.playIfVisible(), { once: true });
  }

  playIfVisible() {
    if (this.hasPlayed) return;

    const rect = this.section.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
    const minimumVisible = Math.min(rect.height * 0.08, viewportHeight * 0.14);

    if (visibleHeight > minimumVisible) {
      this.play();
      this.observer?.disconnect();
    }
  }

  play() {
    if (this.hasPlayed) return;
    this.hasPlayed = true;
    this.section.classList.add('is-visible', 'is-active');
  }

  showFinalState() {
    this.section.classList.add('is-visible', 'is-active', 'is-reduced-motion');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-tos-project-stories]').forEach((section) => {
    new TOSProjectStoriesIntro(section);
  });
});
